import { useEffect } from 'react'
import ConfigProvider from '@/components/ui/ConfigProvider'
import { themeConfig } from '@/configs/theme.config'
import useDarkMode from '@/utils/hooks/useDarkMode'
import useThemeSchema from '@/utils/hooks/useThemeSchema'
import useLocale from '@/utils/hooks/useLocale'
import useDirection from '@/utils/hooks/useDirection'
import { useThemeSync } from '@/hooks/useThemeSync'
import { usePersonalitySync } from '@/hooks/usePersonalitySync'
import { useThemeStore } from '@/store/themeStore'
import { applyThemeVariant } from '@/utils/themeVariant'
import { MODE_DARK, MODE_LIGHT } from '@/constants/theme.constant'
import type { CommonProps } from '@/@types/common'

const Theme = (props: CommonProps) => {
    useThemeSchema()
    const [isDarkMode] = useDarkMode()
    const [direction] = useDirection()

    // After zustand persist rehydrates, re-attach variant class on <html>.
    // User-driven changes already call applyThemeVariant inside setThemeName.
    useEffect(() => {
        const apply = () => {
            const name = useThemeStore.getState().themeName ?? 'light'
            applyThemeVariant(name, false)
        }
        if (useThemeStore.persist?.hasHydrated?.()) apply()
        const unsub = useThemeStore.persist?.onFinishHydration?.(apply)
        return () => {
            unsub?.()
        }
    }, [])
    // Bridge Zustand mode ↔ server preferences.theme so the top-bar
    // toggle survives logout / switches devices and Settings writes
    // stay in sync with the top-bar icon. No-op until preferences load.
    useThemeSync()
    // Pro / Fun, the same way: per user, synced across devices.
    usePersonalitySync()

    const { locale } = useLocale()

    return (
        <ConfigProvider
            value={{
                locale: locale,
                ...themeConfig,
                direction,
                mode: isDarkMode ? MODE_DARK : MODE_LIGHT,
            }}
        >
            {props.children}
        </ConfigProvider>
    )
}

export default Theme
